import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select, { type MultiValue } from 'react-select'
import type { GenericList, OptionsItem } from './models/JobSharedInterfaces';
import { CheckPermissions, DownloadFilesViaPost, GetQuery } from './Shared';

const ReportJobKpisData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [startDate, setStartDate] = React.useState("");
    const [endDate, setEndDate] = React.useState("");
    const [truckId, setTruckId] = React.useState(-1);
    const [scheduleBranchId, setScheduleBranchId] = React.useState(-1);
    const [includeRolloverJobs, setIncludeRolloverJobs] = React.useState(true);
    const [includeAssistedJobs, setIncludeAssistedJobs] = React.useState(true);
    const [truckOptions, setTruckOptions] = React.useState<GenericList[]>([]);
    const [teamOptions, setTeamOptions] = React.useState<OptionsItem[]>([]);
    const [teamFilter, setTeamFilter] = React.useState<OptionsItem[]>([]);
    const [itemOptions, setItemOptions] = React.useState<OptionsItem[]>([]);
    const [itemFilter, setItemFilter] = React.useState<OptionsItem[]>([]);
    const [scheduleBranchOptions, setScheduleBranchOptions] = React.useState<GenericList[]>([]);

    React.useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("RunReports");
        if (hasPermission) {
            getKpiDropdowns();
        } else {
            navigate("/accessdenied")
        }
    }

    const getKpiDropdowns = async () => {
        const apiUrl = "GetJobKpiDropdowns";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setScheduleBranchOptions(result.data.scheduleBranchOptions);
            setScheduleBranchId(result.data.scheduleBranchId);
            setTruckOptions(result.data.truckOptions);
            setTeamOptions(result.data.teamOptions);
            setItemOptions(result.data.itemOptions);
            setLoading(false);
        }
    }

    const getTeamByBranch = async (schedBranchId: number) => {
        const apiUrl = 'GetUsersWithTruckRole?CompanyId=2&ScheduleBranchId=' + schedBranchId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setTeamOptions(result.data);
        }
    }

    const handleBranchChange = (e: any) => {
        let schedBranchId = e.target.value * 1;
        setScheduleBranchId(schedBranchId);
        
        if (schedBranchId > 0) {
            getTeamByBranch(schedBranchId);
        }
    }

    const clearError = (e: any) => {
        var error = errors;
        delete error[e.target.name];
        setErrors(error);
    }

    const handleStartDateChange = (e: any) => {
        clearError(e);
        setStartDate(e.target.value);
    }

    const handleEndDateChange = (e: any) => {
        clearError(e);
        setEndDate(e.target.value);
    }
    //, actionMeta: ActionMeta<OptionsItem>
    const handleMultiTeamChange = (selected: MultiValue<OptionsItem>) => {
        var result = [...selected];
        setTeamFilter(result);
    }
    //, actionMeta: ActionMeta<OptionsItem>
    const handleMultiItemChange = (selected: MultiValue<OptionsItem>) => {
        var result = [...selected];
        setItemFilter(result);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (!startDate) {
            formIsValid = false;
            errorList["startDate"] = "Start Date is required";
        }

        if (!endDate) {
            formIsValid = false;
            errorList["endDate"] = "End Date is required";
        }

        if (startDate && endDate && endDate < startDate) {
            formIsValid = false;
            errorList["startDate"] = "Start Date must be < End Date";
        }

        if (scheduleBranchId < 0) {
            formIsValid = false;
            errorList["scheduleBranchId"] = "Branch is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const runReport = (e: any) => {
        e.preventDefault();
        //run the report and export the data
        if (validate()) {
            runRpt();
        }
    }

    const runRpt = async () => {
        var reportCriteria = {
            startDate: startDate,
            endDate: endDate,
            scheduleBranchId: scheduleBranchId,
            truckId: truckId,
            team: teamFilter,
            items: itemFilter,
            incRolloverJobs: includeRolloverJobs,
            incAssistedJobs: includeAssistedJobs
        };

        var jsonItem = JSON.stringify(reportCriteria);

        const apiUrl = "RunJobKpiReport";
        await DownloadFilesViaPost(apiUrl, jsonItem, 'Job KPIs.xlsx');
    }

    const customStyles = {
        control: (baseStyles: any) => ({
            ...baseStyles,
            flex: 1,
            maxWidth: '450px',
            minWidth: '250px'
        })
    };

    const renderDetails = (
        <div className="kpireport__screen--height">
            <div>
                <h1>Job KPI Report</h1>
            </div>
            <div className="marginTop20">
                <span>Select a Start Date and End Date to run the report. Use the other options for filtering the report.</span>
            </div>
            <div>
                <div className="input-group-parent">
                    <label className="input-group" htmlFor="startDate">
                        <span className="label">Start Date</span>
                        <input type='date' id="startDate" name="startDate" className='input' value={startDate} onChange={(e) => handleStartDateChange(e)}></input>
                    </label>
                    <span className={errors["startDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["startDate"]}</span>

                    <label className="input-group" htmlFor="endDate">
                        <span className="label">End Date</span>
                        <input type='date' id="endDate" name="endDate" className='input' value={endDate} onChange={(e) => handleEndDateChange(e)}></input>
                    </label>
                    <span className={errors["endDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["endDate"]}</span>

                    <label className="input-group" htmlFor="scheduleBranchId">
                        <span className="label">Branch</span>
                        <select className="select" id="scheduleBranchId" name="scheduleBranchId" value={scheduleBranchId} onChange={(e) => handleBranchChange(e)}>
                            {scheduleBranchOptions.map(branch =>
                                <option key={branch.id} value={branch.id}>{branch.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={errors["scheduleBranchId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["scheduleBranchId"]}</span>

                    <div className="marginLeftLabel">
                        <label className="input-group--inline job__checkboxPosition kpireport__checkbox--left" htmlFor="includeRolloverJobs">
                            <span className="label">Include Rollover Jobs</span>
                            <div>
                                <input type="checkbox" className="kpireport__checkbox--margin" name="includeRolloverJobs" id="includeRolloverJobs" checked={includeRolloverJobs} onChange={(e) => setIncludeRolloverJobs(e.target.checked)}></input>
                            </div>
                        </label>
                        <label className="input-group--inline job__checkboxPosition" htmlFor="includeAssistedJobs">
                            <span className="label">Include Assisted Jobs</span>
                            <div>
                                <input type="checkbox" className="kpireport__checkbox--margin" name="includeAssistedJobs" id="includeAssistedJobs" checked={includeAssistedJobs} onChange={(e) => setIncludeAssistedJobs(e.target.checked)}></input>
                            </div>
                        </label>
                    </div>
                </div>
                <div className="input-group-parent">                  
                    <label className="input-group" htmlFor="truckId">
                        <span className="label">Truck</span>
                        <select className="select" id="truckId" name="truckId" value={truckId} onChange={(e) => setTruckId(Number(e.target.value))}>
                            <option defaultValue="-1" value="-1"></option>
                            {truckOptions.map(truck =>
                                <option key={truck.id} value={truck.id}>{truck.name}</option>
                            )};
                        </select>
                    </label>
                    <label className="input-group" htmlFor="teamMemberId">
                        <span className="label">Team</span>
                        <Select styles={customStyles} isMulti id="teamMemberFilter" name="teamMemberFilter" options={teamOptions} onChange={handleMultiTeamChange} />
                    </label>
                    <label className="input-group" htmlFor="itemId">
                        <span className="label">Item/Product</span>
                        <Select styles={customStyles} isMulti id="itemFilter" name="itemFilter" options={itemOptions} onChange={handleMultiItemChange} />
                    </label> 
                </div>
                    
            </div>
            <button className="defaultbutton marginLeftLabel" type='button' onClick={runReport}>Run Report</button>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (<div>
        {contents}
    </div>)

};

export default ReportJobKpisData;